import { useState, useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Card,
  Form,
  AutoComplete,
  Input,
  Select,
  Button,
  Divider,
  Typography,
  Checkbox,
  message,
} from 'antd'
import { RocketOutlined } from '@ant-design/icons'
import { createScheduledTask } from '../store/slices/scheduledTaskSlice'
import billingService from '../services/billingService'
import scheduledTaskService from '../services/scheduledTaskService'
import stockTickerService from '../services/stockTickerService'
import ScheduledTaskPaymentForm from '../components/Payment/ScheduledTaskPaymentForm'
import PricingCalculator from '../components/Pricing/PricingCalculator'
import { RESEARCH_DEPTH_OPTIONS } from '../utils/constants'
import { formatPrice } from '../utils/pricingCalculator'
import './ScheduledTaskNew.css'

const { Option } = Select
const { Title, Text } = Typography

const ANALYST_OPTIONS = [
  { label: 'Market Analyst', value: 'market' },
  { label: 'Social Analyst', value: 'social' },
  { label: 'News Analyst', value: 'news' },
  { label: 'Fundamentals Analyst', value: 'fundamentals' },
]

const FIXED_SCHEDULE_TYPE = 'DAILY'

const ScheduledTaskNew = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [quote, setQuote] = useState(null)
  const [quoteLoading, setQuoteLoading] = useState(false)
  const [subscriptionEstimate, setSubscriptionEstimate] = useState(null)
  const [estimateLoading, setEstimateLoading] = useState(false)
  const [paymentStep, setPaymentStep] = useState(false)
  const [savedFormData, setSavedFormData] = useState(null)
  const [tickerOptions, setTickerOptions] = useState([])
  const allTickersRef = useRef([])

  const quoteTimerRef = useRef(null)
  const estimateTimerRef = useRef(null)
  const lastQuoteParamsRef = useRef(null)
  const lastEstimateParamsRef = useRef(null)

  // Load all tickers once on mount for client-side filtering
  useEffect(() => {
    stockTickerService.getAll().then((data) => {
      allTickersRef.current = data
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (user?.email && !form.getFieldValue('notificationEmail')) {
      form.setFieldValue('notificationEmail', user.email)
    }
  }, [form, user?.email])

  const handleTickerSearch = useCallback((value) => {
    const results = stockTickerService.filter(allTickersRef.current, value)
    setTickerOptions(
      results.map((t) => ({
        value: t.symbol,
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 600 }}>{t.symbol}</span>
            <span style={{ color: '#8c8c8c', fontSize: 12, marginLeft: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
              {t.name}
            </span>
          </div>
        ),
      }))
    )
  }, [])

  // Fetch quote from backend API
  const fetchQuote = useCallback(async (researchDepth, selectedAnalysts) => {
    // Skip if no analysts selected to avoid 422 error
    if (!selectedAnalysts || selectedAnalysts.length === 0) {
      return
    }

    const params = {
      researchDepth,
      selectedAnalysts,
    }

    // Skip if same as last request
    const paramsKey = JSON.stringify(params)
    if (lastQuoteParamsRef.current === paramsKey) {
      return
    }
    lastQuoteParamsRef.current = paramsKey

    try {
      setQuoteLoading(true)
      const response = await billingService.getQuote(params)
      setQuote(response)
    } catch (error) {
      console.error('Failed to fetch quote:', error)
      setQuote(null)
    } finally {
      setQuoteLoading(false)
    }
  }, [])

  // Fetch subscription estimate from backend (v2.0)
  const fetchSubscriptionEstimate = useCallback(async (params) => {
    const paramsKey = JSON.stringify(params)
    if (lastEstimateParamsRef.current === paramsKey) return
    lastEstimateParamsRef.current = paramsKey

    try {
      setEstimateLoading(true)
      const estimate = await scheduledTaskService.estimateSubscription(params)
      if (estimate) {
        setSubscriptionEstimate(estimate)
      }
    } catch (error) {
      console.error('Failed to fetch subscription estimate:', error)
      setSubscriptionEstimate(null)
    } finally {
      setEstimateLoading(false)
    }
  }, [])

  // Prefill form from URL query params (from New Analysis page)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const ticker = params.get('ticker')
    const researchDepth = params.get('researchDepth')
    const analysts = params.getAll('analysts')

    const prefillValues = {}
    if (ticker) prefillValues.ticker = ticker.toUpperCase()
    if (researchDepth) prefillValues.researchDepth = parseInt(researchDepth, 10)
    if (analysts.length > 0) prefillValues.selectedAnalysts = analysts

    if (Object.keys(prefillValues).length > 0) {
      form.setFieldsValue(prefillValues)
    }

    // Merge prefill with form defaults to get current effective values
    const effectiveDepth = prefillValues.researchDepth || form.getFieldValue('researchDepth') || 2
    const effectiveAnalysts = prefillValues.selectedAnalysts || form.getFieldValue('selectedAnalysts') || []
    const effectiveScheduleType = FIXED_SCHEDULE_TYPE

    // Fetch per-task quote
    if (effectiveAnalysts.length > 0) {
      fetchQuote(effectiveDepth, effectiveAnalysts)
    }

    // Fetch monthly subscription estimate (v2.0)
    if (effectiveAnalysts.length > 0 && effectiveScheduleType) {
      fetchSubscriptionEstimate({
        scheduleType: effectiveScheduleType,
        selectedAnalysts: effectiveAnalysts,
        researchDepth: effectiveDepth,
      })
    }
  }, [location.search, form, fetchQuote, fetchSubscriptionEstimate])

  const handleValuesChange = useCallback((changedValues, allValues) => {
    if (
      changedValues.researchDepth !== undefined ||
      changedValues.selectedAnalysts !== undefined
    ) {
      const researchDepth = allValues.researchDepth || 2
      const selectedAnalysts = allValues.selectedAnalysts || []

      // Clear previous timer
      if (quoteTimerRef.current) {
        clearTimeout(quoteTimerRef.current)
      }

      // Debounce quote fetch
      quoteTimerRef.current = setTimeout(() => {
        fetchQuote(researchDepth, selectedAnalysts)
      }, 350)
    }

    // Fetch subscription estimate when any relevant field changes
    if (
      changedValues.researchDepth !== undefined ||
      changedValues.selectedAnalysts !== undefined
    ) {
      const selectedAnalysts = allValues.selectedAnalysts
      const researchDepth = allValues.researchDepth

      if (selectedAnalysts?.length > 0 && researchDepth) {
        if (estimateTimerRef.current) {
          clearTimeout(estimateTimerRef.current)
        }
        estimateTimerRef.current = setTimeout(() => {
          fetchSubscriptionEstimate({
            scheduleType: FIXED_SCHEDULE_TYPE,
            selectedAnalysts,
            researchDepth,
          })
        }, 500)
      }
    }
  }, [fetchQuote, fetchSubscriptionEstimate])

  const handleSubmit = async (values) => {
    try {
      setLoading(true)
      const taskData = {
        ticker: values.ticker.toUpperCase(),
        scheduleType: FIXED_SCHEDULE_TYPE,
        notificationEmail: values.notificationEmail.trim(),
        selectedAnalysts: values.selectedAnalysts,
        researchDepth: values.researchDepth,
        paymentMethodId: values.paymentMethodId,
      }

      await dispatch(createScheduledTask(taskData)).unwrap()
      message.success('Scheduled task created successfully!')
      navigate('/scheduled-tasks')
    } catch (error) {
      console.error('Failed to create scheduled task:', error)
      message.error(error.message || 'Failed to create scheduled task')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateTask = async () => {
    try {
      const values = await form.validateFields()
      // Save form data before moving to payment step
      setSavedFormData(values)
      setPaymentStep(true)
    } catch (error) {
      // Validation failed - user will see error messages on fields
    }
  }

  const handlePaymentSuccess = async (paymentMethodId) => {
    // Use saved form data from before entering payment step
    if (!savedFormData) {
      message.error('Form data not found. Please try again.')
      setPaymentStep(false)
      return
    }

    await handleSubmit({ ...savedFormData, paymentMethodId })
  }

  const handleBack = () => {
    setLoading(false)
    setPaymentStep(false)
    setSavedFormData(null)
  }

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (quoteTimerRef.current) clearTimeout(quoteTimerRef.current)
      if (estimateTimerRef.current) clearTimeout(estimateTimerRef.current)
    }
  }, [])

  // Use saved form data for payment step display
  const displayData = savedFormData || {
    researchDepth: form.getFieldValue('researchDepth') || 2,
    selectedAnalysts: form.getFieldValue('selectedAnalysts') || [],
    scheduleType: FIXED_SCHEDULE_TYPE,
  }

  const researchDepth = displayData.researchDepth || 2
  const selectedAnalysts = displayData.selectedAnalysts || []
  const scheduleType = displayData.scheduleType || FIXED_SCHEDULE_TYPE

  return (
    <div className="scheduled-task-new-page">
      <div className="scheduled-task-new-container">
        {!paymentStep ? (
          <Card className="scheduled-task-form" variant="borderless">
            <div className="task-form__hero">
              <div className="task-form__hero-icon">
                <RocketOutlined />
              </div>
              <div className="task-form__hero-content">
                <Title level={2} className="task-form__hero-title">
                  Subscription Plan
                </Title>
                <Text className="task-form__hero-subtitle">
                  Set up recurring market analysis with automatic billing. Your analysis will run daily, and you will
                  receive an email notification when the decision status changes.
                </Text>
              </div>
            </div>

            <div className="task-form__body">
              <Divider className="task-form__divider" />

              <Form
                form={form}
                layout="vertical"
                className="task-form__form"
                onFinish={handleSubmit}
                onValuesChange={handleValuesChange}
                initialValues={{
                  researchDepth: 2,
                  notificationEmail: user?.email,
                }}
                autoComplete="off"
              >
                <Form.Item
                  label="Ticker Symbol"
                  name="ticker"
                  rules={[
                    { required: true, message: 'Please input ticker symbol.' },
                    {
                      pattern: /^[A-Za-z.]{1,10}$/,
                      message: 'Ticker must be 1-10 letters.',
                    },
                  ]}
                >
                  <AutoComplete
                    options={tickerOptions}
                    onSearch={handleTickerSearch}
                    placeholder="e.g., NVDA, AAPL, TSLA"
                    size="large"
                    style={{ textTransform: 'uppercase' }}
                  />
                </Form.Item>

                <Form.Item
                  label={(
                    <div className="task-form__label">
                      <span>Research Depth</span>
                      <span className="task-form__label-hint">Higher depth provides more thorough analysis.</span>
                    </div>
                  )}
                  name="researchDepth"
                  rules={[{ required: true, message: 'Please select research depth.' }]}
                >
                  <Select size="large" placeholder="Select research depth">
                    {RESEARCH_DEPTH_OPTIONS.map((depth) => (
                      <Option key={depth.value} value={depth.value}>
                        {depth.label} — {depth.description}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label="Notification Email"
                  name="notificationEmail"
                  extra="You'll get an email when this subscription's decision changes."
                  rules={[
                    { required: true, message: 'Please enter an email address.' },
                    { type: 'email', message: 'Please enter a valid email address.' },
                  ]}
                >
                  <Input
                    placeholder="you@example.com"
                    size="large"
                    autoComplete="email"
                  />
                </Form.Item>

                <Form.Item
                  label={(
                    <div className="task-form__label">
                      <span>Analysts</span>
                      <span className="task-form__label-hint">Select which specialists should contribute to the analysis.</span>
                    </div>
                  )}
                  name="selectedAnalysts"
                  rules={[
                    {
                      required: true,
                      message: 'Please select at least one analyst.',
                    },
                  ]}
                >
                  <Checkbox.Group options={ANALYST_OPTIONS} className="task-form__checkbox-group" />
                </Form.Item>

                {/* Per-task pricing (only show when no subscription estimate yet) */}
                {!subscriptionEstimate && !estimateLoading && (
                  <div style={{ marginTop: 18, marginBottom: 0 }}>
                    <PricingCalculator
                      quote={quote}
                      quoteLoading={quoteLoading}
                      billingSummary={null}
                      billingLoading={false}
                    />
                  </div>
                )}

                {/* Loading state for subscription estimate */}
                {estimateLoading && (
                  <div style={{ marginTop: 16, padding: '16px', background: '#f0fdf4', borderRadius: 12, border: '1px solid #bbf7d0', textAlign: 'center' }}>
                    <Typography.Text type="secondary">Calculating monthly subscription...</Typography.Text>
                  </div>
                )}

                {/* Subscription Estimate (v2.0 monthly billing) */}
                {subscriptionEstimate && !estimateLoading && (
                  <div style={{ marginTop: 16, padding: '16px', background: '#f0fdf4', borderRadius: 12, border: '1px solid #bbf7d0' }}>
                    <Typography.Text strong style={{ display: 'block', marginBottom: 8, color: '#166534' }}>
                      Monthly Subscription Pricing
                    </Typography.Text>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <Text type="secondary">Frequency discount</Text>
                      <Text style={{ color: '#16a34a' }}>{subscriptionEstimate.discountLabel}</Text>
                    </div>
                    <Divider style={{ margin: '8px 0' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text strong>Monthly Total</Text>
                      <Text strong style={{ fontSize: 18, color: '#166534' }}>
                        {formatPrice(subscriptionEstimate.estimatedMonthlyAmount, subscriptionEstimate.currency)}
                      </Text>
                    </div>
                  </div>
                )}

                <Form.Item className="task-form__cta">
                  <Button
                    type="primary"
                    htmlType="button"
                    loading={loading}
                    size="large"
                    block
                    icon={<RocketOutlined />}
                    className="task-form__cta-button"
                    onClick={handleCreateTask}
                  >
                    {loading ? 'Creating...' : (() => {
                      if (subscriptionEstimate) {
                        const amount = Number(subscriptionEstimate.estimatedMonthlyAmount ?? 0)
                        const currency = subscriptionEstimate.currency || 'USD'
                        return `Subscribe — ${formatPrice(amount, currency)} / month`
                      }
                      if (quote) {
                        const numericAmount = Number(quote.totalAmount ?? 0)
                        const amountDisplay = Number.isFinite(numericAmount)
                          ? numericAmount.toFixed(2)
                          : quote.totalAmount
                        const currency = quote.currency || 'USD'
                        return `Create Scheduled Task & Pay ${amountDisplay} ${currency} / execution`
                      }
                      return 'Create Scheduled Task'
                    })()}
                  </Button>

                  {/* Secure Payment Note */}
                  {(quote || subscriptionEstimate) && (
                    <div style={{ textAlign: 'center', marginTop: '12px' }}>
                      <Text style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
                        💳 Secure payment via Stripe • Billed monthly
                      </Text>
                    </div>
                  )}

                  {/* One-time analysis link - moved to footer */}
                  <div className="task-form__footer-link">
                    <Text style={{ fontSize: 12, color: '#9ca3af' }}>
                      Prefer a one-time analysis?{' '}
                    </Text>
                    <Button
                      type="link"
                      onClick={() => {
                        const values = form.getFieldsValue()
                        const params = new URLSearchParams()
                        if (values.ticker) params.set('ticker', values.ticker.toUpperCase())
                        if (values.researchDepth) params.set('researchDepth', values.researchDepth)
                        if (values.selectedAnalysts && values.selectedAnalysts.length > 0) {
                          values.selectedAnalysts.forEach(a => params.append('analysts', a))
                        }
                        navigate(`/tasks/new?${params.toString()}`)
                      }}
                      style={{ padding: 0, height: 'auto', fontSize: 12, fontWeight: 500 }}
                    >
                      Create single task →
                    </Button>
                  </div>
                </Form.Item>
              </Form>
            </div>
          </Card>
        ) : (
          <Card className="scheduled-task-payment-card" variant="borderless">
            <div className="task-new-payment-header">
              <h2>Complete Payment</h2>
              <p>
                Set up your recurring billing. The scheduled task will activate after payment confirmation.
              </p>
            </div>

            <ScheduledTaskPaymentForm
              quote={quote}
              subscriptionEstimate={subscriptionEstimate}
              selectedAnalysts={selectedAnalysts}
              researchDepth={researchDepth}
              scheduleType={scheduleType}
              scheduleHour={8}
              scheduleMinute={30}
              onPaymentSuccess={handlePaymentSuccess}
              loading={loading}
              setLoading={setLoading}
            />

            <div className="task-new-payment-footer">
              <span>Payments are processed securely by Stripe · You will be billed monthly based on your subscription plan.</span>
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}

export default ScheduledTaskNew
